import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { MyComponentProps } from 'react-froala-wysiwyg';
import sanitizeHtml from 'sanitize-html';
import { TemplateUsage as TemplateType } from '@bb/domain/dist/types/Template';
import FroalaInput from '@app/react-common/FroalaInput';
import {
  TEMPLATE_TAG_TYPES,
  TEMPLATE_TAG_LABELS,
  TagType,
  convertTagToHtml,
} from 'utils/template';
import { AuthContextType } from 'contexts';
import { OrgFeatures } from '@bb/domain/dist/types';
import { Feature } from '@bb/automaton';
import theme from '../theme';
import antMessage from '../Message';

const TAG_CONTAINER_HEIGHT = '90px';
const SECOND_TOOLBAR_DIFF = '2px';
const ERROR_TOOLBAR_DIFF = '22px';

// Not all tags appear on every type of message
export const TYPE_FILTER_MAP = {
  offer: [
    'coopFirstName',
    'coopLastName',
    'totalOffers',
    'registerOfferLink',
    'listingThumbnailLink',
    'listingAddress',
    'viewListingLink',
    'bookAppointmentLink',
    'listingBrokerageName',
    'listingBrokeragePhone',
    'listingAgentFirstName',
    'listingAgentLastName',
    'listingAgentEmail',
    'listingAgentMobile',
    'sellerFirstName',
    'sellerLastName',
  ],
  feedback: [
    'coopFirstName',
    'coopLastName',
    'appointmentDate',
    'appointmentTime',
    'feedbackLink',
    'listingThumbnailLink',
    'listingAddress',
    'viewListingLink',
    'registerOfferLink',
    'bookAppointmentLink',
    'listingBrokerageName',
    'listingBrokeragePhone',
    'listingAgentFirstName',
    'listingAgentLastName',
    'listingAgentEmail',
    'listingAgentMobile',
    'sellerFirstName',
    'sellerLastName',
  ],
  feedbackSurvey: [],
  adminBlast: [],
  propertyAnnouncement: [
    'coopFirstName',
    'coopLastName',
    'registerOfferLink',
    'listingThumbnailLink',
    'listingAddress',
    'viewListingLink',
    'totalOffers',
    'bookAppointmentLink',
    'listingBrokerageName',
    'listingBrokeragePhone',
    'listingAgentFirstName',
    'listingAgentLastName',
    'listingAgentEmail',
    'listingAgentMobile',
    'sellerFirstName',
    'sellerLastName',
  ],
  sendListing: [
    'coopFirstName',
    'coopLastName',
    'listingThumbnailLink',
    'listingAddress',
    'viewListingLink',
    'bookAppointmentLink',
    'listingBrokerageName',
    'listingBrokeragePhone',
    'listingAgentFirstName',
    'listingAgentLastName',
    'listingAgentEmail',
    'listingAgentMobile',
    'sellerFirstName',
    'sellerLastName',
  ],
  emailSignature: [],
} as const;

const FEATURE_FLAG_TAG_FILTER: Record<string, Array<Feature | `${Feature}`>> = {
  totalOffers: ['OFFERS'],
  registerOfferLink: ['OFFERS'],
};

export interface FroalaTaggedInputProps extends MyComponentProps {
  templateType?: TemplateType;
  disabled?: boolean;
  onClick?: () => void;
  preview?: boolean;
  allowedIframeHostnames?: string[];
  hasFeature?: AuthContextType['hasFeature'];
  dataCy?: string;
  errorMessage?: string | false;
}

// Clean up of faulty pop up served as image.error response from froala. More details on froala errors: https://froala.com/wysiwyg-editor/docs/events#image.error
const onFailedImageUpload = (error: any) => {
  const el = document.getElementsByClassName('fr-popup');
  for (let i = 0; i < el.length; i++) {
    el[i].parentNode.removeChild(el[i]);
  }

  antMessage.warning({
    content: <span>Error occured while uploading image.</span>,
  });
};

export const FroalaTaggedInput = (props: FroalaTaggedInputProps) => {
  const {
    tag,
    model,
    onModelChange,
    config,
    templateType,
    disabled,
    onClick,
    preview,
    allowedIframeHostnames,
    hasFeature = (feature: OrgFeatures) => true,
    dataCy,
    errorMessage,
  } = props;

  const ref = useRef<{ editor: any }>({ editor: null });
  const [isFroalaInitialized, setIsFroalaInitialized] = useState(false);

  // Set up event handling and other things after init
  useEffect(() => {
    if (isFroalaInitialized) {
      const { editor } = ref.current;

      let draggedTag: HTMLElement = null;

      editor.events.on('dragstart', (froalaEvent: any) => {
        const event: DragEvent = froalaEvent.originalEvent;
        if (
          event.target instanceof HTMLSpanElement &&
          event.target.dataset.type
        ) {
          const tagElement = event.target;
          const tagType = tagElement.dataset.type;
          draggedTag = tagElement;
          event.dataTransfer.setData('Text', tagType);
        }
      });

      // Listen to drop events after initialization
      editor.events.on(
        'drop',
        (dropEvent: any) => {
          if (draggedTag) {
            draggedTag.remove();
            draggedTag = null;
          }

          // When a draggable is dropped into Froala, insert the
          // corresponding html at the point where it was dropped

          // Focus at the current posisiton.
          editor.markers.insertAtPoint(dropEvent.originalEvent);
          editor.selection.restore();

          // Save into undo stack the current position.
          if (!editor.undo.canDo()) editor.undo.saveStep();

          // Insert HTML.
          const templateTag = dropEvent.originalEvent.dataTransfer.getData(
            'Text',
          ) as TagType;
          editor.html.insert(convertTagToHtml(templateTag));

          // Save into undo stack the changes.
          editor.undo.saveStep();

          // Stop event propagation.
          dropEvent.preventDefault();
          dropEvent.stopPropagation();
          return false;
        },
        true,
      );
    }
  }, [isFroalaInitialized]);

  // Must reinitialize froala when we exit preview
  useEffect(() => {
    setIsFroalaInitialized(false);
  }, [preview]);

  useEffect(() => {
    if (!isFroalaInitialized || !ref.current?.editor?.edit) return;
    if (disabled) ref.current.editor.edit.off();
    else ref.current.editor.edit.on();
  }, [preview, disabled, isFroalaInitialized]);

  const filteredTags: TagType[] = useMemo(() => {
    // Must spread the readonly array into a normal one
    if (!templateType) return [...TEMPLATE_TAG_TYPES];
    return [...TYPE_FILTER_MAP[templateType]];
  }, [templateType]);

  const showError = useMemo(() => !disabled && isFroalaInitialized && Boolean(errorMessage), [disabled, isFroalaInitialized, errorMessage]);

  if (preview) {
    const sanitizedBody = sanitizeHtml(model as string, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat([
        'img',
        'span',
        'wbr',
        ...(allowedIframeHostnames ? ['iframe'] : []),
      ]),
      allowedAttributes: false,
      allowedIframeHostnames: [
        'www.youtube.com',
        'player.vimeo.com',
        ...(allowedIframeHostnames || []),
      ],
    });
    return (
      <Container
        preview
        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        onClick={onClick}
        data-cy={`${dataCy}-preview`}
      />
    );
  }

  const featureFlagFilter = (filteredTag: string) =>
    filteredTag in FEATURE_FLAG_TAG_FILTER
      ? FEATURE_FLAG_TAG_FILTER[filteredTag].every(feature =>
          hasFeature(feature),
        )
      : true;

  return (
    <Container
      disabled={disabled}
      onClick={onClick}
      data-cy={`${dataCy}-container`}
    >
      <StyledErrorContainer error={showError}>
        <FroalaInput
          ref={ref}
          tag={tag}
          model={model}
          onModelChange={!disabled && onModelChange}
          config={{
            events: {
              initialized: (initEvent: any) => {
                setIsFroalaInitialized(true);
              },
              'image.error': onFailedImageUpload,
            },
            ...config,
          }}
        />
      </StyledErrorContainer>
      {showError && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      {!disabled && isFroalaInitialized && (
        <TagPanel data-cy={`${dataCy}-tag-panel`} error={showError}>
          <AddTagsLabel>Add Tags</AddTagsLabel>
          <TemplateTagsContainer>
            {filteredTags.filter(featureFlagFilter).map((t: TagType) => (
              <TemplateTag type={t} key={t} />
            ))}
          </TemplateTagsContainer>
        </TagPanel>
      )}
    </Container>
  );
};

export default FroalaTaggedInput;

const TemplateTag = (props: { type: TagType }) => {
  const { type } = props;
  return (
    <div
      className={`template-tag`}
      key={type}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('Text', type);
      }}
    >
      {TEMPLATE_TAG_LABELS[type]}
    </div>
  );
};

const StyledErrorContainer = styled.div<{ error?: boolean }>`
  ${({ error }) => error && 'border: 1px solid #ff4d4f; border-radius: 10px;'}
`;

const StyledErrorMessage = styled.div`
  height: 15px;
  color: #ff4d4f;
  margin-left: 5px;
  margin-top: 5px;
`;

const TagPanel = styled.div<{ error?: boolean }>`
  border-top: 1px solid #ccc;

  transform: translateY(
    calc(
      -${TAG_CONTAINER_HEIGHT} -
        ${({ error }) => (error ? ERROR_TOOLBAR_DIFF : SECOND_TOOLBAR_DIFF)}
    )
  );

  height: ${TAG_CONTAINER_HEIGHT};
`;

const TemplateTagsContainer = styled.div`
  padding: 0px 1px;
  display: flex;
  flex-flow: row wrap;
  overflow-y: scroll;
  /* The leftover height after accounting for the 20px label */
  height: calc(${TAG_CONTAINER_HEIGHT} - 20px);

  && {
    .template-tag {
      margin: 5px;
    }
  }
`;

const AddTagsLabel = styled.div`
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
  color: ${theme.gray7};
  margin-left: 5px;
`;

const Container = styled.div<{
  disabled?: boolean;
  preview?: boolean;
}>`
  margin: ${({ preview }) => (preview ? '10px' : 'auto')};

  /* Our copy of Inter doesn't support italics properly; this can be removed if that changes */
  ${({ preview }) => (preview ? 'font-family: sans-serif;' : '')}

  .fr-view.fr-disabled {
    background-color: ${theme.gray3};
  }

  && {
    .fr-view img.fr-dib {
      display: inline;
      vertical-align: middle;
    }
  }

  p {
    margin-bottom: 0;
  }

  em {
    font-style: italic;
  }

  ul,
  ol {
    display: block;
    list-style: disc outside none;
    margin: 1em 0;
    padding: 0 0 0 40px;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    display: list-item;
  }

  ul ul,
  ol ul {
    list-style-type: circle;
    margin-left: 15px;
  }

  ol ol,
  ul ol {
    list-style-type: lower-latin;
    margin-left: 15px;
  }

  ${({ disabled }) =>
    disabled
      ? ''
      : `
      .second-toolbar {
        padding-top: ${TAG_CONTAINER_HEIGHT};
      }
  `}

  .template-tag {
    padding: 1px 4px;
    margin: 0px;

    color: ${theme.geekblue6};
    font-size: 12px;
    line-height: 20px;

    background-color: ${theme.geekblue1};
    opacity: 0.85;

    border: 1px solid ${theme.geekblue3};
    border-radius: 4px;

    cursor: grab;
  }
`;
